import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiPlusCircleOutline } from "@mdi/js";
import { Routes, Route, useLocation } from "react-router-dom";
import { MainLayout, ListSidebar } from "layouts";
import { Button, CircularProgress } from "@mui/material";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { navigate, HandleError } from "shared/utils";
import { NotistackServices, McpLibraryServices, PublicContractedMcpDTO } from "shared/services";
import { ToolLibraryContent } from "./ToolLibraryContent";
import { ToolManagementContent } from "./ToolManagementContent";

export const ToolsPage: React.FC = () => {
	const [sidebarSearch, setSidebarSearch] = useState("");
	const location = useLocation();

	const [contractedMcps, setContractedMcps] = useState<PublicContractedMcpDTO[]>([]);
	const [loadingContractedMcps, setLoadingContractedMcps] = useState(true);

	const timeRef = useRef<NodeJS.Timeout>(undefined);

	// Extrai apenas o contractId da URL (/tools/:toolId)
	const activeToolId = useMemo(() => {
		const match = location.pathname.match(/^\/tools\/([^/]+)$/);
		return match?.[1] ?? null;
	}, [location.pathname]);

	const loadContractedMcps = useCallback(() => {
		setLoadingContractedMcps(true);
		clearTimeout(timeRef.current);
		timeRef.current = setTimeout(async () => {
			try {
				const data = await McpLibraryServices.listMcpsHired();
				setContractedMcps(data);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar MCPs contratados");
			} finally {
				setLoadingContractedMcps(false);
			}
		}, 1000);
	}, []);

	useEffect(() => {
		loadContractedMcps();
		return () => clearTimeout(timeRef.current);
	}, [loadContractedMcps]);

	useLayoutEffect(() => {
		const timeout = setTimeout(() => {
			const params = new URLSearchParams(location.search);
			const status = params.get("mcp_oauth2_status");
			if (!status) return;

			const message = params.get("mcp_oauth2_message");
			if (status === "success") {
				NotistackServices.showSuccessMessage("Conta vinculada com sucesso");
			} else {
				NotistackServices.showErrorMessage(message || "Falha ao vincular conta OAuth2");
			}

			loadContractedMcps();

			["mcp_oauth2_status", "mcp_oauth2_message", "mcp_oauth2_contract_id", "mcp_oauth2_env", "mcp_oauth2_provider"].forEach((key) =>
				params.delete(key),
			);

			const cleanedQuery = params.toString();
			window.history.replaceState(window.history.state, "", `${location.pathname}${cleanedQuery ? `?${cleanedQuery}` : ""}`);
		}, 500);

		return () => clearTimeout(timeout);
	}, [location.pathname, location.search, loadContractedMcps]);

	useEffect(() => {
		if (!activeToolId || loadingContractedMcps) return;
		const exists = contractedMcps.some((contract) => String(contract.id) === activeToolId);
		if (!exists) {
			navigate("/tools");
		}
	}, [activeToolId, contractedMcps, loadingContractedMcps]);

	// Filtro do sidebar
	const filteredContractedMcps = useMemo(() => {
		if (!sidebarSearch.trim()) return contractedMcps;
		const term = sidebarSearch.toLowerCase();
		return contractedMcps.filter((contract) => {
			const mcpName = contract.mcp?.name ?? "";
			const category = contract.mcp?.category ?? "";
			return mcpName.toLowerCase().includes(term) || category.toLowerCase().includes(term);
		});
	}, [sidebarSearch, contractedMcps]);

	const hiredContractMap = useMemo(() => {
		return contractedMcps.reduce(
			(acc, contract) => {
				acc[String(contract.mcpId)] = String(contract.id);
				return acc;
			},
			{} as Record<string, string>,
		);
	}, [contractedMcps]);

	const sidebar = (
		<ListSidebar
			searchPlaceholder="Buscar ferramentas..."
			onSearch={setSidebarSearch}
			footer={
				<Button
					variant="outlined"
					color="primary"
					fullWidth
					className={styles.sidebarAddBtn}
					disableElevation
					onClick={() => navigate("/tools")}
					startIcon={
						<Icon
							path={mdiPlusCircleOutline}
							size={0.8}
						/>
					}
				>
					Adicionar ferramenta
				</Button>
			}
		>
			{loadingContractedMcps ? (
				<div className={styles.sidebarLoading}>
					<CircularProgress size={24} />
				</div>
			) : filteredContractedMcps.length === 0 ? (
				<div className={styles.sidebarEmpty}>
					<p>Nenhuma ferramenta contratada</p>
				</div>
			) : (
				filteredContractedMcps.map((contract) => {
					const mcpName = contract.mcp?.name ?? "MCP";
					const slug = contract.mcp?.slug ?? "";
					const statusInfo = !contract.isActive
						? { label: "Inativo", className: "statusInactive" }
						: { label: "Ativo", className: "statusActive" };

					return (
						<div
							key={contract.id}
							className={clsx(styles.toolCard, activeToolId === String(contract.id) && styles.toolCardActive)}
							onClick={() => navigate(`/tools/${contract.id}`)}
						>
							<div className={styles.toolCardInfo}>
								<div className={styles.toolCardRow}>
									<span className={styles.toolCardName}>{mcpName}</span>
									<span className={clsx(styles.toolCardStatus, styles[statusInfo.className])}>{statusInfo.label}</span>
								</div>
								<p className={styles.toolCardDesc}>{slug}</p>
							</div>
						</div>
					);
				})
			)}
		</ListSidebar>
	);

	return (
		<MainLayout sidebar={sidebar}>
			<Routes>
				<Route
					index
					element={
						<ToolLibraryContent
							hiredContractMap={hiredContractMap}
							onManageContract={(contractId) => navigate(`/tools/${contractId}`)}
							onOpenMcp={(mcpId) => navigate(`/tools/mcp/${mcpId}`)}
						/>
					}
				/>
				<Route
					path="mcp/:mcpId"
					element={
						<ToolManagementContent
							contractedMcps={contractedMcps}
							loadingList={loadingContractedMcps}
							onUpdate={loadContractedMcps}
							onContractUpdated={(updated) =>
								setContractedMcps((current) => current.map((item) => (String(item.id) === String(updated.id) ? { ...item, ...updated } : item)))
							}
							onContractRemoved={(id) => setContractedMcps((current) => current.filter((item) => String(item.id) !== String(id)))}
							onHired={(contract) => {
								setContractedMcps((current) => [contract, ...current.filter((item) => String(item.id) !== String(contract.id))]);
								navigate(`/tools/${contract.id}`);
							}}
						/>
					}
				/>
				<Route
					path=":toolId"
					element={
						<ToolManagementContent
							contractedMcps={contractedMcps}
							loadingList={loadingContractedMcps}
							onUpdate={loadContractedMcps}
							onContractUpdated={(updated) =>
								setContractedMcps((current) => current.map((item) => (String(item.id) === String(updated.id) ? { ...item, ...updated } : item)))
							}
							onContractRemoved={(id) => setContractedMcps((current) => current.filter((item) => String(item.id) !== String(id)))}
							onHired={(contract) => {
								setContractedMcps((current) => [contract, ...current.filter((item) => String(item.id) !== String(contract.id))]);
								navigate(`/tools/${contract.id}`);
							}}
						/>
					}
				/>
			</Routes>
		</MainLayout>
	);
};
